/* Dashboard grid — multi-panel situational-awareness layout.
   Design concept inspired by World Monitor (AGPL-3.0); panel implementations original.

   Layout single-source-of-truth: the working grid layout lives in the
   `useChat` zustand store (see store.ts / layout.ts), so the LLM dashboard
   actions (add_panel / move_panel / remove_panel) mutate the same layout the
   grid renders. Drag/resize edits flow back into the store via setLayout. */

import { useQuery } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react"
// Use the v1-compatible legacy API (flat props, WidthProvider HOC).
import { Responsive, WidthProvider, type Layout } from "react-grid-layout/legacy"
import { api } from "./api"
import { CommandPalette } from "./CommandPalette"
import { useCommands } from "./commands"
import type { DashboardPayload } from "./dashboard-types"
import { sectionIsDemo } from "./demo"
import {
  clearStoredLayout,
  DEFAULT_LAYOUT,
  LAYOUT_STORAGE_KEY,
  mergeLayoutWithDynamic,
  responsiveLayout,
} from "./layout"
import { Panel } from "./Panel"
import { WorkspaceChrome } from "./WorkspaceChrome"
import { WorkspaceOverview } from "./WorkspaceOverview"
import { RotateCcw } from "lucide-react"
import { RegionDeepDive } from "./RegionDeepDive"
import { useApp, useChat, useRealtimeStore, useRegion } from "./store"
import { useRealtime } from "./useRealtime"
import { Toasts } from "./Toasts"
import { BUILTIN_DEFAULT_ID, useViews, type SavedView } from "./useViews"
import { ViewsMenu } from "./ViewsMenu"
import { ChatPanel } from "./panels/ChatPanel"
import { DisruptionFeed } from "./panels/DisruptionFeed"
import { DynamicPanel } from "./panels/DynamicPanel"
import { EconomicPressure } from "./panels/EconomicPressure"
import { EscalationMonitor } from "./panels/EscalationMonitor"
import { FleetPosture } from "./panels/FleetPosture"
import { GlobePanel } from "./panels/GlobePanel"
import { InfrastructureCascade } from "./panels/InfrastructureCascade"
import { LaneInstability } from "./panels/LaneInstability"
import { MarketTicker } from "./panels/MarketTicker"
import { PortConditions } from "./panels/PortConditions"
import { RiskGauge } from "./panels/RiskGauge"
import { SignalAggregator } from "./panels/SignalAggregator"
import { StreamGrid } from "./panels/StreamGrid"
import { AiForecasts } from "./panels/AiForecasts"
import { AiMarketImplications } from "./panels/AiMarketImplications"
import { ForcePostureDetailed } from "./panels/ForcePostureDetailed"
import { LiveIntelligence } from "./panels/LiveIntelligence"
import { TradeCounter } from "./panels/TradeCounter"
import { PredictionsSummary } from "./panels/PredictionsSummary"
import { WarehouseOps } from "./panels/WarehouseOps"

const REFRESH_MS = 45_000

// Re-exported for backwards compatibility — canonical home is layout.ts.
export { DEFAULT_LAYOUT, LAYOUT_STORAGE_KEY }

const ResponsiveGridLayout = WidthProvider(Responsive)

/* Which DOM elements should swallow a bare "k" keystroke (so users can type
   the letter inside a text field without spawning the palette). */
function isTextInput(el: Element | null): boolean {
  if (!el) return false
  const tag = el.tagName
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true
  if (el instanceof HTMLElement && el.isContentEditable) return true
  return false
}

/* Whether the user has asked the OS to minimise motion. Read once per mount;
   the pulse cue degrades to a static dot when true. */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/* The header status dot, with a brief one-shot pulse ring each time the
   realtime stream delivers an envelope. The ring is keyed off the store's
   monotonic `pulse` counter so a fresh event re-mounts (and thus re-runs) the
   ring animation; unrelated re-renders never re-trigger it. Reduced-motion
   users get the plain dot. Keyframes live in an inline <style> on purpose —
   index.css is owned elsewhere. */
function LivePulseDot({ className }: { className: string }) {
  const pulse = useRealtimeStore((s) => s.pulse)
  const reduced = prefersReducedMotion()
  const animate = pulse > 0 && !reduced

  return (
    <span className="relative inline-flex items-center justify-center">
      {animate && (
        <>
          <style>{
            "@keyframes sl-live-ping{0%{transform:scale(1);opacity:.55}" +
            "70%{transform:scale(2.6);opacity:0}100%{transform:scale(2.6);opacity:0}}"
          }</style>
          <span
            // key by `pulse` → React remounts this node every event, restarting
            // the one-shot ring; `forwards` holds it invisible until the next.
            key={pulse}
            aria-hidden
            className="absolute h-2 w-2 rounded-full pointer-events-none"
            style={{
              background: "#44ff88",
              animation: "sl-live-ping 900ms ease-out forwards",
            }}
          />
        </>
      )}
      <span className={`status-dot ${className} relative`} />
    </span>
  )
}

export function Dashboard() {
  const tab = useApp((s) => s.tab)
  const setTab = useApp((s) => s.setTab)
  const setPaletteOpen = useApp((s) => s.setPaletteOpen)
  const resetLayoutNonce = useApp((s) => s.resetLayoutNonce)

  // The store owns the working layout (and persists it); the chat action
  // reducer mutates the same state, so LLM layout edits render immediately.
  const layout = useChat((s) => s.layout)
  const setLayout = useChat((s) => s.setLayout)

  // Live SSE liveness layer — opens one EventSource and pulses the store on
  // each envelope. No-ops gracefully when the stream never connects (demo).
  useRealtime()

  const viewsApi = useViews()
  const { data, isLoading, isError, error, refetch, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get<DashboardPayload>("/api/dashboard"),
    refetchInterval: REFRESH_MS,
  })

  const ageSec = useMemo(
    () => (dataUpdatedAt ? Math.max(0, Math.floor((Date.now() - dataUpdatedAt) / 1000)) : 0),
    [dataUpdatedAt],
  )

  const handleLayoutChange = useCallback(
    (next: Layout) => {
      setLayout(next)
    },
    [setLayout],
  )

  const { setActiveViewId } = viewsApi
  const handleResetLayout = useCallback(() => {
    clearStoredLayout()
    setLayout(DEFAULT_LAYOUT)
    // Reset means "fall back to the baseline" — match the active-view label to Default.
    setActiveViewId(BUILTIN_DEFAULT_ID)
  }, [setLayout, setActiveViewId])

  // When a named view is chosen from the menu, copy its layout into the
  // working layout so the grid re-renders and the RESET button keeps its
  // existing semantics (RESET continues to fall back to DEFAULT_LAYOUT).
  const handleLoadView = useCallback(
    (view: SavedView) => {
      const cloned: Layout = view.layout.map((item) => ({ ...item }))
      setLayout(cloned)
    },
    [setLayout],
  )

  // Command-palette view-load: call viewsApi.loadView (to update activeViewId)
  // AND Dashboard's handleLoadView (to re-render the grid). Keeps the palette's
  // "Load view: X" path identical to clicking the view in the menu.
  const handlePaletteLoadView = useCallback(
    (view: SavedView) => {
      const loaded = viewsApi.loadView(view.id)
      if (loaded) handleLoadView(loaded)
    },
    [viewsApi, handleLoadView],
  )

  // React to palette-triggered resets: the nonce flips; we run the existing reset.
  const lastNonceRef = useRef(resetLayoutNonce)
  useEffect(() => {
    if (resetLayoutNonce !== lastNonceRef.current) {
      lastNonceRef.current = resetLayoutNonce
      handleResetLayout()
    }
  }, [resetLayoutNonce, handleResetLayout])

  // Global ⌘K / Ctrl-K binding. We also allow bare "k" when not typing, so
  // power users on either platform get the shortcut without modifiers.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Never hijack when a deeper handler has already acted on the event.
      if (e.defaultPrevented) return

      const mod = e.metaKey || e.ctrlKey
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault()
        // Toggle — a second press collapses the palette.
        setPaletteOpen(!useApp.getState().paletteOpen)
        return
      }
      // Bare "k" only if no modifiers, user isn't typing, and nothing else
      // is covering the viewport (palette / region modal already open).
      if (
        (e.key === "k" || e.key === "K") &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !isTextInput(document.activeElement) &&
        !useApp.getState().paletteOpen &&
        !useRegion.getState().activeRegion
      ) {
        e.preventDefault()
        setPaletteOpen(true)
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [setPaletteOpen])

  const commands = useCommands({
    views: viewsApi.views,
    onLoadView: handlePaletteLoadView,
  })

  const viewTools = <>
    <ViewsMenu views={viewsApi.views} activeViewId={viewsApi.activeViewId} saveView={viewsApi.saveView}
      loadView={viewsApi.loadView} deleteView={viewsApi.deleteView} currentLayout={layout} onLoadLayout={handleLoadView} />
    <button type="button" onClick={handleResetLayout} className="btn-pill reset-layout" title="Restore the default panel arrangement"><RotateCcw size={12} />Reset layout</button>
  </>

  return (
    <WorkspaceChrome
      status={<><LivePulseDot className={isLoading ? "connecting" : isError ? "down" : "live"} /><span>{isLoading ? "Loading data" : isError ? "Data refresh unavailable" : "API connected"}</span></>}
      tools={<a href="https://github.com/koala73/worldmonitor" target="_blank" rel="noopener noreferrer">Inspired by World Monitor · AGPL-3.0</a>}
    >
      {tab === "cockpit" ? (
        <div className="workspace-scroll">
          <WorkspaceOverview data={data} refreshing={isFetching} onRefresh={() => void refetch()} tools={viewTools} />
          <CockpitGrid data={data} loading={isLoading} isError={isError}
            errorMessage={error instanceof Error ? error.message : undefined} onRetry={() => void refetch()}
            layout={layout} onLayoutChange={handleLayoutChange} />
        </div>
      ) : (
        <div className="world-workspace"><div className="world-heading"><div className="eyebrow">A CONNECTED WORLD</div><h1>Follow the global picture.</h1><p>Explore flights, shipping lanes and disruption signals.</p></div><div className="world-map"><GlobePanel /></div></div>
      )}
      <RegionDeepDive />
      <Toasts />
      <CommandPalette commands={commands} />
    </WorkspaceChrome>
  )
}

interface CockpitGridProps {
  data?: DashboardPayload
  loading: boolean
  isError: boolean
  errorMessage?: string
  onRetry: () => void
  layout: Layout
  onLayoutChange: (layout: Layout) => void
}

function CockpitGrid({
  data,
  loading,
  isError,
  errorMessage,
  onRetry,
  layout,
  onLayoutChange,
}: CockpitGridProps) {
  // react-grid-layout mutates width via ResizeObserver; if the scroll container is suspended
  // (tab swap), we want the grid to re-measure on mount. Keying by a mount-nonce forces that.
  const activeBreakpoint = useRef<string | null>(null)
  const mountRef = useRef(0)
  useEffect(() => {
    mountRef.current += 1
  }, [])

  // Dynamic panels created by the LLM chat tool-calls. The store owns the spec;
  // we merge any ids that aren't already in the layout so the grid has a
  // placement for them on first render.
  const dynamicPanels = useChat((s) => s.dynamicPanels)

  const mergedLayout = useMemo<Layout>(
    () => mergeLayoutWithDynamic(layout, Object.keys(dynamicPanels)),
    [layout, dynamicPanels],
  )

  if (isError && !data) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="ui-panel ui-panel--glass max-w-md w-full rounded p-6 text-center">
          <div className="text-xs uppercase tracking-widest text-red-400 font-bold">
            dashboard unreachable
          </div>
          <p className="mt-3 text-sm text-[color:var(--text-muted)] leading-relaxed">We couldn’t refresh your dashboard. Check your connection and try again.</p>
          {errorMessage && (
            <p className="mt-2 text-[10px] text-[color:var(--text-dim)] font-mono break-all">
              {errorMessage}
            </p>
          )}
          <button type="button" onClick={onRetry} className="btn-pill mt-4 px-4">
            retry
          </button>
        </div>
      </div>
    )
  }

  if (loading || !data) {
    return (
      <div className="flex-1 flex items-center justify-center text-[color:var(--text-dim)] text-xs uppercase tracking-widest">
        loading dashboard…
      </div>
    )
  }

  const demo = (key: string, section: Parameters<typeof sectionIsDemo>[2]) =>
    sectionIsDemo(data, key, section)
  const marketDemo =
    demo("market", data.market) ||
    demo(
      "market_data",
      data.market_data ? Object.values(data.market_data).flat() : undefined,
    )

  /* Panel registry: the grid renders exactly the panels present in the
     layout, so add_panel / remove_panel visibly change the cockpit. Returns
     null when a known panel's payload section is absent (panel hidden). */
  const renderPanel = (id: string): ReactNode | null => {
    switch (id) {
      case "streams":
        return <StreamGrid streams={data.streams} demo={demo("streams", data.streams)} />
      case "fleet":
        return <FleetPosture theaters={data.fleet_posture} demo={demo("fleet_posture", data.fleet_posture)} />
      case "lanes":
        return <LaneInstability lanes={data.lanes} demo={demo("lanes", data.lanes)} />
      case "risk":
        return <RiskGauge risk={data.risk_overview} demo={demo("lanes", data.lanes) || data.demo_sections?.includes("risk_overview")} />
      case "intel":
        return <DisruptionFeed items={data.disruption_feed} demo={demo("disruption_feed", data.disruption_feed)} />
      case "market":
        return (
          <MarketTicker
            rows={data.market}
            marketData={data.market_data}
            predictions={data.predictions?.items}
            demo={marketDemo}
          />
        )
      case "infra":
        return <InfrastructureCascade data={data.infrastructure} />
      case "signals":
        return <SignalAggregator signals={data.signals} demo={demo("signals", data.signals)} />
      case "escalation":
        return <EscalationMonitor rows={data.escalation} demo={demo("escalation", data.escalation)} />
      case "ai-forecasts":
        return data.ai_forecasts ? (
          <AiForecasts items={data.ai_forecasts} demo={demo("ai_forecasts", data.ai_forecasts)} />
        ) : null
      case "live-intel":
        return data.live_intelligence ? (
          <LiveIntelligence
            items={data.live_intelligence}
            demo={demo("live_intelligence", data.live_intelligence)}
          />
        ) : null
      case "trade-counter":
        return data.trade_counter ? (
          <TradeCounter data={data.trade_counter} demo={demo("trade_counter", data.trade_counter)} />
        ) : null
      case "ai-market":
        return data.ai_market_implications ? (
          <AiMarketImplications
            data={data.ai_market_implications}
            demo={demo("ai_market_implications", data.ai_market_implications)}
          />
        ) : null
      case "force-posture":
        return data.force_posture_detailed ? (
          <ForcePostureDetailed
            items={data.force_posture_detailed}
            demo={demo("force_posture_detailed", data.force_posture_detailed)}
          />
        ) : null
      case "port-conditions":
        return data.port_conditions ? (
          <PortConditions
            ports={data.port_conditions}
            portPredictions={data.predictions?.ports}
            demo={demo("port_conditions", data.port_conditions)}
          />
        ) : null
      case "predictions":
        return data.predictions ? <PredictionsSummary predictions={data.predictions} /> : null
      case "economic":
        return <EconomicPressure items={data.economic} demo={demo("economic", data.economic)} />
      case "warehouse":
        return <WarehouseOps />
      case "globe":
        return <GlobePanel />
      case "chat":
        return <ChatPanel />
      default: {
        const spec = dynamicPanels[id]
        if (spec) return <DynamicPanel spec={spec} />
        return <UnknownPanel id={id} />
      }
    }
  }

  // Responsive grid only needs a single breakpoint for our use case; we keep the 12-col grid
  // at every width. Panel content handles its own internal responsiveness.
  return (
    <div className="cockpit-grid">
      <ResponsiveGridLayout
        className="layout"
        layouts={{ lg: mergedLayout, md: responsiveLayout(mergedLayout, 6), sm: responsiveLayout(mergedLayout, 1) }}
        breakpoints={{ lg: 1100, md: 650, sm: 0 }}
        cols={{ lg: 12, md: 6, sm: 1 }}
        rowHeight={52}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        compactType="vertical"
        draggableHandle=".panel-drag-handle"
        isDraggable
        isResizable
        resizeHandles={["se"]}
        useCSSTransforms
        onBreakpointChange={(breakpoint) => { activeBreakpoint.current = breakpoint }}
        onLayoutChange={(next) => { if (activeBreakpoint.current === "lg") onLayoutChange(next) }}
      >
        {mergedLayout.flatMap((item) => {
          const content = renderPanel(item.i)
          if (content == null) return []
          return [<div key={item.i} data-panel-id={item.i}>{content}</div>]
        })}
      </ResponsiveGridLayout>
    </div>
  )
}

/* Rendered when the layout references a panel type with no registered
   renderer (e.g. the LLM added an id we don't know). Honest placeholder so
   the add_panel action is still visible and reversible. */
function UnknownPanel({ id }: { id: string }) {
  return (
    <Panel title={id}>
      <div className="px-3 py-4 text-[11px] text-[color:var(--text-dim)] leading-relaxed">
        No renderer exists for panel type "{id}". Ask the assistant to remove
        it ("remove the {id} panel") or use list_panels for valid types.
      </div>
    </Panel>
  )
}
